/**
 * 67.73 (the full-size map, DIRECTION §0.11: *"the far look of bases and settlements, terraced compounds seen from far off"*):
 * bases and structures seen from far away — from a hundred people out to across the planet, and from the commander's
 * diorama (68.14). Before this every structure drew its full model at any range (renderer.ts createStructureMesh: no tier).
 *
 * Three tiers past the full model, chosen by the structure's size on screen (its diameter in pixels, so the diorama's
 * narrow lens keeps the full model further out, as it should):
 *   - **far** — a merged low-poly of the kind (tens to a few hundred triangles): the HQ's compound, containers, fly and
 *     masts; the pad's matting, circle and windsock; the parked dropship's hull, nacelles and box; the FOB's U and net;
 *     the relay's mast and pennant; a modular building or compound generated from its layout's own module boxes
 *     (core/modules.ts posesOf, constants/modules.ts MODULE_SHAPES), its wall runs merged and its openings dark bands;
 *   - **silhouette** — the same masses, coarser (no openings, no small parts): what reads at a few pixels;
 *   - nothing past a pixel or so, but the lights.
 * Every far shape is drawn instanced: one draw per kind per tier for the whole planet, the team's colour per instance
 * (a vertex's `aTeam` share takes it). **The lights** that carry at night — the HQ's root mast, the pad's edge lights, the
 * FOB's mast and lamps, the relay's light, a building's lit windows — are one point draw for the planet, a few pixels
 * whatever the range, blinking where the near lamps blink, faint or out by day. **A struck structure's smoke** is one
 * instanced draw of rising puffs, sized in the world, seen from afar. Lamps and smoke stand in for a structure only while
 * its full model is not drawn (its own lamps and smoke take over near).
 *
 * Lengths in people (a person 1.8 model units); the host passes `personWorld` (world units a person). Presentation only.
 */
import * as THREE from 'three';
import type { BuildingLayout, ModuleKind } from '../types';
import { posesOf, layoutExtent } from '../core/modules';
import { MODULE_SHAPES, MODULE_SLAB } from '../constants/modules';
import { DROPSHIP } from './dropshipLooks';
import { masonryFarColors, type MasonryLook } from './baseKit';

/** 69.3b's frame fit raised the airframe (people) over the hit shape's numbers measured before it. */
const DROPSHIP_UP = (DROPSHIP.lift - 0.15) / 1.8;

/** The tiers by screen size (the structure's diameter in device pixels), their hysteresis, and the lights' and smoke's dials. */
export const FAR_LOD = {
  /** the full model down to this many pixels across; the far tier to `silhouettePx`; the silhouette to `dotPx` */
  fullPx: 48, silhouettePx: 9, dotPx: 1.2,
  /** a tier holds this share past its edge (no flicker at the line) */
  hysteresis: 0.12,
  /** a lamp is drawn at least this many pixels across, at most `lampMaxPx`; its world size (people) sets it near */
  lampMinPx: 3.5, lampMaxPx: 14,
  /** a building's windows lit at night: this share of them */
  windowsLit: 0.45,
  /** smoke: puffs a source, a column's height and a puff's size (people), its rise time (s); a puff at least `puffMinPx` across */
  puffs: 16, column: 70, puff: 9, rise: 14, puffMinPx: 5,
} as const;

/** The far looks' colours (the near models' own: footholdLooks.ts, baseKit.ts). */
const C = {
  gravel: '#8c8676', sand: '#b6a47c', sandDark: '#9a8a66', olive: '#5a5d3c', oliveDark: '#3f4229', canvas: '#7d7656', tan: '#a8956a',
  steel: '#5e615e', dark: '#26282a', matting: '#5b5f5e', paint: '#e8e4d4', orange: '#d8652a', hull: '#6d726f',
  ply: '#a8916a', concrete: '#9a978d', roof: '#7d7f78', earth: '#7a6448', wood: '#8a6a44', crate: '#6b6a3e', glass: '#22282c',
  amber: '#ffb347', warm: '#ffd38a', white: '#f4f6ff', fire: '#ff7a2a',
} as const;

export type FarTier = 'full' | 'far' | 'silhouette' | 'none';
/** The kinds with a hand-made far look; a modular building or compound is `layout:<key>` (its layout passed with it). */
export type FootholdFarKind = 'hq' | 'landingPad' | 'fob' | 'beacon' | 'supply' | 'relay' | 'hmg' | 'dropship' | 'cram' | 'ciws' | 'site' | 'generator';   // (69.9: the point-defence mounts by variant; 69.50 step 2a: the air-defence site)

/** A structure as the far field draws it. `team`: its side's colour (null: none — a settlement's). */
export interface FarEntry {
  id: number | string;
  kind: FootholdFarKind | `layout:${string}`;
  layout?: BuildingLayout;
  /** its base's centre on the ground (world) and its turn (up = the ground's normal, yaw included) */
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  team: THREE.Color | null;
  form?: 'whole' | 'damaged' | 'destroyed';
  /** the relay's / the web's light: lit (default), or out */
  lit?: boolean;
}

// ---- the shapes ------------------------------------------------------------------------------------------------------------

/** A lamp on a shape: where (people, the shape's frame), its colour, world size (people), blink rate (Hz; 0 steady), day share. */
interface FarLamp { x: number; y: number; z: number; color: string; size: number; blink: number; day: number; team?: boolean }
/** A kind's far and silhouette geometries, its radius (people, from its base centre), its lamps and where its smoke rises. */
interface FarProto { far: THREE.BufferGeometry; silhouette: THREE.BufferGeometry; radius: number; lamps: FarLamp[]; smoke: [number, number, number] }

/** Triangles with flat normals, vertex colours and a team share — the far tiers' one vertex format. */
class ShapeBuilder {
  private pos: number[] = []; private col: number[] = []; private team: number[] = [];
  private readonly c = new THREE.Color();
  private tri(a: number[], b: number[], d: number[], color: string, team: number): void {
    this.c.set(color);
    for (const v of [a, b, d]) { this.pos.push(v[0], v[1], v[2]); this.col.push(this.c.r, this.c.g, this.c.b); this.team.push(team); }
  }
  /** A quad a→b→c→d (counter-clockwise seen from its front). */
  quad(a: number[], b: number[], c: number[], d: number[], color: string, team = 0): void { this.tri(a, b, c, color, team); this.tri(a, c, d, color, team); }
  /** An axis-aligned box from (x0, y0, z0) to (x1, y1, z1); `bottom` its underside too (off: it stands on the ground). */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, color: string, team = 0, bottom = false): void {
    const q = (a: number[], b: number[], c: number[], d: number[]) => this.quad(a, b, c, d, color, team);
    q([x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]);   // top
    q([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]);   // +z
    q([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]);   // −z
    q([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]);   // +x
    q([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]);   // −x
    if (bottom) q([x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]);
  }
  /** A tapered mast: a square section `w0` wide at y0 to `w1` at y1, centred at (x, z). */
  mast(x: number, z: number, y0: number, y1: number, w0: number, w1: number, color: string, team = 0): void {
    const a = w0 / 2, b = w1 / 2, q = (p: number[], r: number[], s: number[], t: number[]) => this.quad(p, r, s, t, color, team);
    q([x - a, y0, z + a], [x + a, y0, z + a], [x + b, y1, z + b], [x - b, y1, z + b]);
    q([x + a, y0, z - a], [x - a, y0, z - a], [x - b, y1, z - b], [x + b, y1, z - b]);
    q([x + a, y0, z + a], [x + a, y0, z - a], [x + b, y1, z - b], [x + b, y1, z + b]);
    q([x - a, y0, z - a], [x - a, y0, z + a], [x - b, y1, z + b], [x - b, y1, z - b]);
    q([x - b, y1, z - b], [x - b, y1, z + b], [x + b, y1, z + b], [x + b, y1, z - b]);
  }
  /** A gable roof along z: eaves at ±hx and y0, the ridge at y1, from z0 to z1 (its two slopes and gables). */
  gable(hx: number, y0: number, y1: number, z0: number, z1: number, color: string, team = 0): void {
    this.quad([-hx, y0, z1], [0, y1, z1], [0, y1, z0], [-hx, y0, z0], color, team);
    this.quad([hx, y0, z0], [0, y1, z0], [0, y1, z1], [hx, y0, z1], color, team);
    this.tri([-hx, y0, z1], [hx, y0, z1], [0, y1, z1], color, team);
    this.tri([hx, y0, z0], [-hx, y0, z0], [0, y1, z0], color, team);
  }
  /** A flat ring on the ground (a painted circle): `n` segments from r0 to r1 at height y. */
  ring(r0: number, r1: number, y: number, n: number, color: string, team = 0): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2, b = ((i + 1) / n) * Math.PI * 2;
      this.quad([Math.cos(a) * r0, y, Math.sin(a) * r0], [Math.cos(a) * r1, y, Math.sin(a) * r1], [Math.cos(b) * r1, y, Math.sin(b) * r1], [Math.cos(b) * r0, y, Math.sin(b) * r0], color, team);
    }
  }
  /** A flat panel facing ±x or ±z: `axis` the normal's axis and sign, centred at (x, y, z), `w` across and `h` tall. */
  panel(axis: 'x' | '-x' | 'z' | '-z', x: number, y: number, z: number, w: number, h: number, color: string, team = 0): void {
    const a = w / 2, b = h / 2;
    if (axis === 'z') this.quad([x - a, y - b, z], [x + a, y - b, z], [x + a, y + b, z], [x - a, y + b, z], color, team);
    else if (axis === '-z') this.quad([x + a, y - b, z], [x - a, y - b, z], [x - a, y + b, z], [x + a, y + b, z], color, team);
    else if (axis === 'x') this.quad([x, y - b, z + a], [x, y - b, z - a], [x, y + b, z - a], [x, y + b, z + a], color, team);
    else this.quad([x, y - b, z - a], [x, y - b, z + a], [x, y + b, z + a], [x, y + b, z - a], color, team);
  }
  get triangles(): number { return this.pos.length / 9; }
  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('aTeam', new THREE.Float32BufferAttribute(this.team, 1));
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }
}

/** The HQ (footholdLooks.ts hq, with hqRoot.ts's root mast off its front-left corner): `fine` the far tier, else the silhouette. */
function hqShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  const H = 3.67, T = 0.29, h = 0.75;
  b.box(-4, 0, -4, 4, 0.07, 4, C.gravel);
  b.box(-H - T, 0, H - T, H + T, h, H + T, C.sand);                     // the Hesco: north, east, west
  b.box(H - T, 0, -H - T, H + T, h, H - T, C.sand); b.box(-H - T, 0, -H - T, -H + T, h, H - T, C.sand);
  b.box(0.89, 0, -H - T, H - T, h, -H + T, C.sand); b.box(-H + T, 0, -H - T, -0.89, h, -H + T, C.sand);   // the back, its gate
  if (fine) b.box(-1.44, 0, -4.85, 1.44, h, -4.27, C.sandDark);          // the chicane
  b.box(-1.96, 0, -1.35, -0.6, 1.44, 2.02, C.olive);                     // the two containers
  b.box(0.6, 0, -1.35, 1.96, 1.44, 2.02, C.oliveDark);
  b.gable(2.06, 1.46, 1.97, -1.5, 2.17, C.canvas);                       // the fly
  if (fine) {
    b.box(-0.55, 1.98, -0.1, 0.55, 2.0, 1.0, C.canvas, 1);               // the marker panel on the ridge (the team's)
    b.box(-3.2, 0, -2.4, -2.5, 0.65, -0.6, C.oliveDark);                 // the generators under their net
    b.box(-3.3, 1.1, -2.6, -2.3, 1.14, -0.4, C.oliveDark);
    b.mast(2.56, 2.56, 0, 1.6, 0.25, 0.07, C.steel);                     // the dish's mast and the dish
    b.box(2.2, 1.55, 2.3, 2.92, 1.95, 2.6, C.paint);
    b.mast(1.06, -3.8, 0, 1.78, 0.04, 0.03, C.steel);                    // the flag at the gate
    b.box(1.06, 1.33, -3.82, 1.4, 1.75, -3.78, C.canvas, 1);
  }
  b.mast(-4.4, 4.4, 0, 6, fine ? 0.9 : 0.6, 0.2, C.steel);                // the root mast
  if (fine) b.box(-4.4, 5.4, 4.38, -3.6, 5.8, 4.42, C.canvas, 1);         // its pennant
  lamps.push({ x: -4.4, y: 6.15, z: 4.4, color: C.white, size: 0.5, blink: 0, day: 0.25, team: true });
}

/** The landing pad: matting, the circle and the marks, the windsock's pole, the sandbag post; its 16 edge lights blink. */
function padShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  const S = 3.89;
  b.box(-S, 0, -S, S, 0.06, S, C.matting);
  if (fine) {
    b.ring(2.14, 2.33, 0.065, 16, C.paint);
    b.box(-0.89, 0.06, -0.14, 0.89, 0.065, 0.14, C.paint); b.box(-1.03, 0.06, -1.05, -0.75, 0.065, 1.05, C.paint); b.box(0.75, 0.06, -1.05, 1.03, 0.065, 1.05, C.paint);
    b.box(-S + 0.22, 0.06, -S + 0.22, -S + 1.11, 0.065, -S + 0.67, C.paint, 1);
    b.box(-S - 1.4, 0, -S + 0.6, -S - 0.5, 0.37, -S + 1.6, C.sand);      // the marshaller's post
    b.mast(S + 0.67, S + 0.67, 0, 2, 0.05, 0.04, C.steel);               // the windsock
    b.box(S + 0.62, 1.8, S + 0.67, S + 1.3, 1.95, S + 0.73, C.orange);
  }
  for (const t of [-3.33, -1.11, 1.11, 3.33]) for (const [x, z] of [[t, -S], [t, S], [-S, t], [S, t]] as const) lamps.push({ x, y: 0.12, z, color: C.amber, size: 0.18, blink: 1, day: 0.15 });
}

/** The parked dropship (dropshipAnchors' hit shape and footprint, the airframe raised `UP` by 69.3b's frame fit), its troop box under it. */
function dropshipShape(b: ShapeBuilder, fine: boolean): void {
  const U = DROPSHIP_UP;
  b.box(-0.794, 0, -1.861, 0.794, 1.556, 1.611, C.olive);                // the container
  b.box(-1.0, 1.94 + U, -2.17, 1.0, 2.65 + U, 2.28, C.hull, 1);                   // the body, in the side's colour
  b.box(-0.72, 2.06 + U, 0.94, 0.72, 3.0 + U, 2.67, C.hull, 1);                   // the cab
  if (fine) {
    b.box(0.2, 2.6 + U, 2.0, 0.6, 2.95 + U, 2.68, C.glass); b.box(-0.6, 2.6 + U, 2.0, -0.2, 2.95 + U, 2.68, C.glass);
    for (const sx of [-1, 1]) b.box(sx > 0 ? 1.0 : -1.39, 1.98 + U, -2.03, sx > 0 ? 1.39 : -1.0, 2.58 + U, 1.58, C.hull);   // the sponsons
    b.box(-0.61, 2.08 + U, -2.75, 0.61, 2.61 + U, -1.83, C.hull);                 // the tail
    for (const sx of [-1, 1]) for (const z of [0.972, -1.417]) b.box(sx * 1.694 - 0.22, 2.07 + U, z - 0.75, sx * 1.694 + 0.22, 2.6 + U, z + 0.75, C.steel);   // the nacelles
    for (const sx of [-1, 1]) for (const z of [0.417, -1.778]) b.mast(sx * 1.336, z, 0, 1.94 + U, 0.14, 0.1, C.dark);   // the legs
  } else for (const sx of [-1, 1]) b.box(sx > 0 ? 1.0 : -1.92, 2.07 + U, -2.17, sx > 0 ? 1.92 : -1.0, 2.6 + U, 1.72, C.steel);   // nacelles and sponsons as one
}

/** The FOB: the Hesco U open to the rear, the net over its stores, the radio's mast; its lamps. */
function fobShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  const I = 1.03, T = 0.26, h = 0.67;
  b.box(-I - T, 0, -0.75, -I + T, h, I + T, C.sand); b.box(I - T, 0, -0.75, I + T, h, I + T, C.sand); b.box(-I - T, 0, I - T, I + T, h, I + T, C.sand);
  b.box(-0.75, 0, -0.2, 0.75, fine ? 0.5 : 0.9, 0.95, C.oliveDark);
  if (fine) { b.box(-0.75, 0.94, -0.2, 0.75, 0.99, 0.95, C.oliveDark); b.box(-0.28, 0.99, 0.11, 0.28, 1.0, 0.5, C.canvas, 1); }
  b.mast(0.58, -0.58, 0, 1.9, 0.12, 0.05, C.steel);
  lamps.push({ x: 0.58, y: 1.95, z: -0.58, color: C.white, size: 0.3, blink: 0, day: 0.2, team: true });
  for (const x of [-I, I]) lamps.push({ x, y: 0.96, z: I, color: C.amber, size: 0.16, blink: 0.8, day: 0.1 });
}

/** The relay (relayModel.ts): its case, the mast, the head and the pennant in the side's colour; its light. */
function relayShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  b.box(-0.2, 0, -0.14, 0.2, 0.3, 0.14, C.oliveDark);
  b.mast(0, 0, 0.25, 2.15, 0.12, 0.06, C.steel);
  if (fine) { b.box(-0.35, 1.98, -0.03, 0.35, 2.03, 0.03, C.steel); b.box(0.04, 1.6, -0.01, 0.6, 1.9, 0.01, C.canvas, 1); }
  else b.box(0.04, 1.5, -0.03, 0.75, 1.95, 0.03, C.canvas, 1);
  lamps.push({ x: 0, y: 2.24, z: 0, color: C.white, size: 0.35, blink: 0, day: 0.25, team: true });
}

/** The beacon: tripod and pole, its case, the strobe. */
function beaconShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  b.mast(0, 0, 0, 1.7, fine ? 0.5 : 0.35, 0.05, C.steel);
  b.box(0.2, 0, -0.15, 0.55, 0.3, 0.15, C.oliveDark, fine ? 0 : 1);
  if (fine) b.box(-0.18, 1.45, -0.02, 0.18, 1.6, 0.02, C.paint);
  lamps.push({ x: 0, y: 1.75, z: 0, color: C.white, size: 0.25, blink: 1.6, day: 0.35 });
}

/** The supply dump: two pallets of crates under a net, a marker stake. */
function supplyShape(b: ShapeBuilder, fine: boolean): void {
  b.box(-0.75, 0, -0.35, -0.05, 0.62, 0.35, C.olive); b.box(0.05, 0, -0.35, 0.75, fine ? 0.48 : 0.62, 0.35, C.oliveDark);
  if (fine) b.mast(0.95, 0.5, 0, 0.9, 0.04, 0.03, C.orange);
}

/** The sandbag HMG pit (hmgModel.ts's ring) and the gun. */
function hmgShape(b: ShapeBuilder, fine: boolean): void {
  const r = 0.93, n = fine ? 8 : 5;
  for (let i = 0; i < n; i++) {
    if (fine && i === 6) continue;                                           // (open to the rear)
    const a = (i / n) * Math.PI * 2, x = Math.cos(a) * r, z = Math.sin(a) * r;
    b.box(x - 0.24, 0, z - 0.24, x + 0.24, 0.37, z + 0.24, C.sandDark);
  }
  if (fine) b.box(-0.06, 0.37, -0.1, 0.06, 0.5, 0.65, C.dark);
}

/**
 * 69.9: the point-defence mounts (pointDefenceLooks.ts, people): the C-RAM's trailer in the side's colour, its shelter, the
 * turret and its gun, the search radar on its mast; the CIWS's deck, ring, turret and dome. A red light on the radar at night.
 */
function cramShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  b.box(-1.0, 0.3, -1.55, 1.0, 0.61, 1.6, C.hull, 1);                     // the trailer's bed
  b.box(-0.53, 0.61, -1.45, 0.53, 1.2, -0.6, C.oliveDark);               // the generator shelter
  b.box(-0.45, 0.61, 0.1, 0.45, 1.3, 0.9, C.hull, 1);                     // the turret
  if (fine) {
    for (const sx of [-1, 1]) for (const z of [1.17, -1.39]) b.mast(sx * 0.92, z, 0, 0.42, 0.1, 0.06, C.orange);   // the jacks
    b.box(-0.07, 1.12, 0.7, 0.07, 1.26, 2.05, C.dark);                     // the barrels
    b.mast(0.31, -1.28, 1.2, 1.75, 0.08, 0.06, C.steel);                   // the mast
  }
  b.box(-0.08, 1.75, -1.42, 0.7, 2.13, -1.32, C.paint);                    // the search array
  lamps.push({ x: 0.31, y: 2.2, z: -1.28, color: '#ff3a2a', size: 0.22, blink: 0.7, day: 0.1 });
}
function ciwsShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  b.box(-0.95, 0, -0.95, 0.95, 0.2, 0.95, C.steel);                        // the deck
  b.box(-0.55, 0.2, -0.55, 0.55, 0.42, 0.55, C.dark);                       // the ring
  b.box(-0.42, 0.42, -0.5, 0.42, 1.05, 0.45, C.hull, 1);                    // the mount
  b.mast(0, -0.1, 1.0, 2.05, 0.55, 0.5, C.paint);                          // the dome
  if (fine) { b.box(-0.07, 0.85, 0.3, 0.07, 0.97, 1.3, C.dark); b.box(0.6, 0.2, -0.75, 0.98, 0.72, -0.4, C.olive); }   // the barrels; the cabinet
  lamps.push({ x: 0, y: 2.12, z: -0.1, color: '#ff3a2a', size: 0.2, blink: 0.7, day: 0.1 });
}

/** 69.50 step 2a: the forward air-defence site (pointDefenceLooks.ts siteBase, people): its deck, shelter, mast and dome, the launcher raised; a red light on the dome. */
function siteShape(b: ShapeBuilder, fine: boolean, lamps: FarLamp[]): void {
  b.box(-1.55, 0, -2.3, 1.55, 0.14, 3.4, C.steel);                         // the deck
  b.box(-0.78, 0.14, -2.09, 0.78, 1.36, -0.81, C.hull, 1);                  // the shelter
  b.mast(0, 0, 0.14, 5.55, 0.28, 0.22, C.steel);                           // the mast
  b.mast(0, 0, 5.55, 7.3, 0.62, 0.55, C.paint);                            // the dome
  b.box(-0.47, 0.6, 1.7, 0.47, 1.85, 3.3, C.hull, 1);                       // the launcher, raised
  if (fine) b.box(-0.62, 0.14, 1.8, 0.62, 0.45, 3.0, C.dark);               // its turntable
  lamps.push({ x: 0, y: 7.45, z: 0, color: '#ff3a2a', size: 0.3, blink: 0.7, day: 0.1 });
}

/** 69.50 step 2b: the site's generator (pointDefenceLooks.ts buildGenerator, people): its skid, its enclosure in the side's colour, its stack. */
function generatorShape(b: ShapeBuilder, fine: boolean): void {
  b.box(-0.53, 0, -1.22, 0.53, 0.12, 1.22, C.dark);                         // the skid
  b.box(-0.5, 0.12, -1.11, 0.5, 1.17, 0.89, C.hull, 1);                     // the enclosure
  if (fine) b.mast(0.28, -0.72, 1.17, 1.6, 0.07, 0.06, C.steel);           // the stack
}

/** The module kinds a far building draws, their colour; the rest (floors, posts, wire, furniture, stairs) are not seen. */
const LAYOUT_KINDS: Partial<Record<ModuleKind, string>> = {
  wallPlain: C.ply, wallDoor: C.ply, wallWindow: C.ply, wallSlit: C.ply, roof: C.roof, gate: C.steel, gateWide: C.steel,
  tWall: C.concrete, hesco: C.sand, sandbags: C.sandDark, sandbagCorner: C.sandDark, containerRoom: C.olive, tower: C.wood, crate: C.crate,
};
/** The far tier's openings: a dark band on both faces of a wall (people, in its frame: across, from, to). */
const OPENINGS: Partial<Record<ModuleKind, [number, number, number]>> = {
  wallWindow: [0.6, MODULE_SLAB + 0.9, MODULE_SLAB + 1.5], wallDoor: [0.6, MODULE_SLAB, MODULE_SLAB + 1.5], wallSlit: [0.5, 1.1, 1.4],
};
const WALL_LIKE = new Set<ModuleKind>(['wallPlain', 'wallDoor', 'wallWindow', 'wallSlit']);

interface Aabb { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number; color: string }
/** Merge boxes that share a colour and a cross-section and touch or overlap end to end — a wall's run of panels, a roof's slabs, become one box. */
function mergeRuns(list: Aabb[]): Aabb[] {
  const e = 1e-4, same = (a: number, b: number) => Math.abs(a - b) < e;
  let out = list.slice(), merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < out.length; i++) for (let j = i + 1; j < out.length; j++) {
      const a = out[i], b = out[j];
      if (a.color !== b.color || !same(a.y0, b.y0) || !same(a.y1, b.y1)) continue;
      let m: Aabb | null = null;
      if (same(a.z0, b.z0) && same(a.z1, b.z1) && a.x0 <= b.x1 + e && b.x0 <= a.x1 + e) m = { ...a, x0: Math.min(a.x0, b.x0), x1: Math.max(a.x1, b.x1) };
      else if (same(a.x0, b.x0) && same(a.x1, b.x1) && a.z0 <= b.z1 + e && b.z0 <= a.z1 + e) m = { ...a, z0: Math.min(a.z0, b.z0), z1: Math.max(a.z1, b.z1) };
      if (m) { out[i] = m; out.splice(j, 1); merged = true; break outer; }
    }
  }
  return out;
}

/** A module's boxes posed in its building's frame (people): its MODULE_SHAPES parts turned by its quarter turn, as boxes (`fine`: the far tier, else the silhouette). */
function poseBoxes(kind: ModuleKind, x: number, y: number, z: number, yaw: number, fine: boolean, look?: { wall: string; roof: string }): Aabb[] {
  const color = look && WALL_LIKE.has(kind) ? look.wall : look && kind === 'roof' ? look.roof : LAYOUT_KINDS[kind]!, c = Math.round(Math.cos(yaw)), s = Math.round(Math.sin(yaw));
  // (the far look of a wall is its whole panel, the opening a band on it; a tower is its platform and parapet as one, on one leg)
  const parts = WALL_LIKE.has(kind) ? [{ centre: [0, 1, 0], half: [1, 1, 0.12] }]
    : kind === 'tower' ? [{ centre: [0, 1.25, 0], half: [0.25, 1.25, 0.25] }, { centre: [0, 2.94, 0], half: [1.14, 0.44, 1.14] }]
    : kind === 'containerRoom' ? [{ centre: [0, 1, 0], half: [1.95, 1, 1.26] }]
    : !fine && kind === 'sandbags' ? [{ centre: [0, 0.505, 0], half: [1, 0.505, 0.28] }]   // (the silhouette: a parapet's run unbroken)
    : MODULE_SHAPES[kind].filter(p => !p.leaf);
  return parts.map(p => {
    const cx = p.centre[0] * c + p.centre[2] * s, cz = -p.centre[0] * s + p.centre[2] * c;
    const hx = Math.abs(p.half[0] * c) + Math.abs(p.half[2] * s), hz = Math.abs(p.half[0] * s) + Math.abs(p.half[2] * c);
    return { x0: x + cx - hx, y0: y + p.centre[1] - p.half[1], z0: z + cz - hz, x1: x + cx + hx, y1: y + p.centre[1] + p.half[1], z1: z + cz + hz, color };
  });
}

/** A building or compound from its layout: the far tier (wall runs merged, openings dark, lit windows) or the silhouette. */
function layoutShape(b: ShapeBuilder, layout: BuildingLayout, fine: boolean, lamps: FarLamp[], look?: { wall: string; roof: string }): void {
  const ext = layoutExtent(layout), boxes: Aabb[] = [];
  for (const p of posesOf(layout)) {
    if (!LAYOUT_KINDS[p.kind] || (!fine && (p.kind === 'crate' || p.kind === 'sandbagCorner'))) continue;
    boxes.push(...poseBoxes(p.kind, p.x, p.y, p.z, p.yaw, fine, look));
    const o = OPENINGS[p.kind];
    if (!o) continue;
    // its face's normal along z for a quarter turn of 0 or 2, along x for 1 or 3; outward: away from the building's centre
    const alongX = Math.round(Math.cos(p.yaw)) !== 0, out = alongX ? Math.sign(p.z) || 1 : Math.sign(p.x) || 1, edge = alongX ? Math.abs(p.z) > ext.halfZ - 1.2 : Math.abs(p.x) > ext.halfX - 1.2;
    const y = p.y + (o[1] + o[2]) / 2, hgt = o[2] - o[1];
    if (fine) for (const side of [-1, 1]) {
      if (alongX) b.panel(side > 0 ? 'z' : '-z', p.x, y, p.z + side * 0.135, 2 * o[0], hgt, C.glass);
      else b.panel(side > 0 ? 'x' : '-x', p.x + side * 0.135, y, p.z, 2 * o[0], hgt, C.glass);
    }
    // a lit window at night: on its outside face, a keyed share of them
    const key = (Math.imul(p.index + 1, 2654435761) >>> 0) / 4294967296;
    if (fine && p.kind === 'wallWindow' && edge && key < FAR_LOD.windowsLit) {
      lamps.push(alongX ? { x: p.x, y, z: p.z + out * 0.2, color: C.warm, size: 0.6, blink: 0, day: 0 } : { x: p.x + out * 0.2, y, z: p.z, color: C.warm, size: 0.6, blink: 0, day: 0 });
    }
    // a lamp over each outside door (a store or a trailer with no window still shows it is there at night)
    if (fine && p.kind === 'wallDoor' && edge) {
      const over = p.y + MODULE_SLAB + 1.7;
      lamps.push(alongX ? { x: p.x, y: over, z: p.z + out * 0.25, color: C.warm, size: 0.4, blink: 0, day: 0 } : { x: p.x + out * 0.25, y: over, z: p.z, color: C.warm, size: 0.4, blink: 0, day: 0 });
    }
  }
  for (const a of mergeRuns(boxes)) b.box(a.x0, a.y0, a.z0, a.x1, a.y1, a.z1, a.color);
}

/** The geometries and lamps of a kind (built once; shared by every structure of it). */
function buildProto(kind: FarEntry['kind'], layout: BuildingLayout | undefined): FarProto {
  const lamps: FarLamp[] = [], none: FarLamp[] = [];
  const make = (fine: boolean): THREE.BufferGeometry => {
    const b = new ShapeBuilder(), into = fine ? lamps : none;
    switch (kind) {
      case 'hq': hqShape(b, fine, into); break;
      case 'landingPad': padShape(b, fine, into); break;
      case 'dropship': dropshipShape(b, fine); break;
      case 'fob': fobShape(b, fine, into); break;
      case 'relay': relayShape(b, fine, into); break;
      case 'beacon': beaconShape(b, fine, into); break;
      case 'supply': supplyShape(b, fine); break;
      case 'hmg': hmgShape(b, fine); break;
      case 'cram': cramShape(b, fine, into); break;
      case 'ciws': ciwsShape(b, fine, into); break;
      case 'site': siteShape(b, fine, into); break;
      case 'generator': generatorShape(b, fine); break;
      default: if (layout) layoutShape(b, layout, fine, into, kind.includes('@') ? masonryFarColors(kind.split('@')[1] as MasonryLook) : undefined);   // (72.5: a settlement's house in its look, `layout:<key>@<look>`)
    }
    return b.geometry();
  };
  const far = make(true), silhouette = make(false);
  // its radius about the base centre (the tiers' size): the far geometry's extent across and up
  const pos = far.getAttribute('position') as THREE.BufferAttribute;
  let r = 0, top = 0;
  for (let i = 0; i < pos.count; i++) { r = Math.max(r, Math.hypot(pos.getX(i), pos.getZ(i))); top = Math.max(top, pos.getY(i)); }
  return { far, silhouette, radius: Math.max(r, top / 2), lamps, smoke: [0, Math.max(1, top * 0.8), 0] };
}

const protos = new Map<string, FarProto>();
/** The far shapes of a kind (a layout's by its key), built on first use. */
export function farProto(kind: FarEntry['kind'], layout?: BuildingLayout): FarProto {
  let p = protos.get(kind);
  if (!p) { p = buildProto(kind, layout); protos.set(kind, p); }
  return p;
}

/** The triangles of a kind's far tier and silhouette (the cost table). */
export function farTriangles(kind: FarEntry['kind'], layout?: BuildingLayout): { far: number; silhouette: number; lamps: number } {
  const p = farProto(kind, layout);
  return { far: p.far.getAttribute('position').count / 3, silhouette: p.silhouette.getAttribute('position').count / 3, lamps: p.lamps.length };
}

// ---- the materials ---------------------------------------------------------------------------------------------------------

/** The far tiers' one material: lit, flat, the vertex colour mixed toward the instance's (the team's) by `aTeam`. */
function farMaterial(): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ color: '#ffffff', vertexColors: true, flatShading: true });
  m.onBeforeCompile = sh => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aTeam;')
      .replace('#include <color_vertex>', '#if defined( USE_INSTANCING_COLOR )\n  vColor = mix( color, instanceColor.xyz, aTeam );\n#else\n  vColor = color;\n#endif');
  };
  m.customProgramCacheKey = () => 'farStructures';
  return m;
}

const LAMP_VERT = /* glsl */ `
attribute vec3 aColor; attribute float aPhase; attribute float aBlink; attribute float aSize; attribute float aShown; attribute float aDay;
uniform float uTime; uniform float uNight; uniform float uFocal; uniform float uMinPx; uniform float uMaxPx;
uniform float uSunOn; uniform vec3 uCentre; uniform vec3 uSunDir;
varying vec3 vLampColor; varying float vLampA;
// (night where the lamp stands: the sun's height over its horizon, as skyHaze.ts daylight reads it at the camera)
float nightAt(vec3 p) {
  if (uSunOn < 0.5) return uNight;
  float d = clamp((dot(normalize(p - uCentre), uSunDir) + 0.12) / 0.4, 0.0, 1.0);
  return 1.0 - d * d * (3.0 - 2.0 * d);
}
#include <common>
#include <logdepthbuf_pars_vertex>
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  #include <logdepthbuf_vertex>
  float px = aSize * uFocal / max(-mv.z, 1e-3);
  float on = aBlink > 0.0 ? step(0.45, fract(uTime * aBlink + aPhase)) : 1.0;
  float a = aShown * on * mix(aDay, 1.0, nightAt(position));
  vLampColor = aColor; vLampA = a * clamp(px / uMinPx, 0.85, 1.0);
  gl_PointSize = a > 0.001 ? clamp(px * 2.5, uMinPx, uMaxPx) : 0.0;
}`;
const LAMP_FRAG = /* glsl */ `
varying vec3 vLampColor; varying float vLampA;
#include <logdepthbuf_pars_fragment>
void main() {
  #include <logdepthbuf_fragment>
  float d = length(gl_PointCoord - 0.5) * 2.0;
  if (d > 1.0) discard;
  float core = smoothstep(1.0, 0.0, d);
  gl_FragColor = vec4(vLampColor * (0.6 + 0.8 * core * core), core * vLampA);
}`;

const SMOKE_VERT = /* glsl */ `
attribute vec3 aBase; attribute vec3 aUp; attribute vec3 aSide; attribute float aPhase; attribute float aScale; attribute float aShown;
uniform float uTime; uniform float uRise; uniform float uColumn; uniform float uPuff; uniform float uFocal; uniform float uMinPx;
uniform float uNight; uniform float uSunOn; uniform vec3 uCentre; uniform vec3 uSunDir;
varying float vSmokeA; varying vec2 vUv; varying float vLow; varying float vNight;
#include <common>
#include <fog_pars_vertex>
#include <logdepthbuf_pars_vertex>
void main() {
  float t = fract(uTime / uRise + aPhase);
  vec3 c = aBase + aUp * (t * uColumn * aScale) + aSide * (t * t * uColumn * 0.35 * aScale);
  vec4 mvPosition = modelViewMatrix * vec4(c, 1.0);
  float size = max(uPuff * aScale * (0.45 + 1.4 * t), uMinPx * (0.6 + t) * -mvPosition.z / uFocal);   // (it reads at any range)
  mvPosition.xy += position.xy * size;
  gl_Position = projectionMatrix * mvPosition;
  #include <logdepthbuf_vertex>
  vSmokeA = aShown * smoothstep(0.0, 0.12, t) * (1.0 - t) * 0.75;
  vUv = position.xy; vLow = (1.0 - t) * (1.0 - t) * (1.0 - t);
  float dl = clamp((dot(normalize(aBase - uCentre), uSunDir) + 0.12) / 0.4, 0.0, 1.0);
  vNight = uSunOn > 0.5 ? 1.0 - dl * dl * (3.0 - 2.0 * dl) : uNight;
  #include <fog_vertex>
}`;
const SMOKE_FRAG = /* glsl */ `
uniform vec3 uSmoke; uniform vec3 uSmokeNight; uniform vec3 uFire; varying float vSmokeA; varying vec2 vUv; varying float vLow; varying float vNight;
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
void main() {
  #include <logdepthbuf_fragment>
  float d = length(vUv) * 2.0;
  if (d > 1.0) discard;
  // (at night its foot glows with the fire under it)
  gl_FragColor = vec4(mix(mix(uSmoke, uSmokeNight, vNight), uFire, vLow * vNight * 0.7), vSmokeA * smoothstep(1.0, 0.2, d));
  #include <fog_fragment>
}`;

// ---- the field -------------------------------------------------------------------------------------------------------------

interface Slot { entry: FarEntry; proto: FarProto; tier: FarTier; hidden: boolean; lamp0: number; lamps: number; puff0: number; puffs: number; centre: THREE.Vector3; up: THREE.Vector3 }
interface KindMeshes { far: THREE.InstancedMesh; silhouette: THREE.InstancedMesh; slots: Slot[] }

/** The far field of a planet's structures: set its entries, update it each frame; it says which keep their full model. */
export class FarStructures {
  readonly group = new THREE.Group();
  private readonly material = farMaterial();
  private readonly kinds = new Map<string, KindMeshes>();
  private slots: Slot[] = [];
  private readonly byId = new Map<number | string, Slot>();
  private lamps: THREE.Points | null = null;
  private smoke: THREE.Mesh | null = null;
  /** 69.98 (DECISIONS 319, the owner: "Yes, build it"): the lamps' and the plumes' materials, made once and kept across
   * `setEntries` — a rebuild (a crash's wrecks, any structure set's change) disposed them and compiled both programs again
   * in that frame: the only shader work at a shootdown's hit and its impact (lane F's strike measure) */
  private lampMat: THREE.ShaderMaterial | null = null;
  private smokeMat: THREE.ShaderMaterial | null = null;
  /** where it is night: by the sun over each lamp's and plume's ground when the host gives it (play), else one `night` for all (the sheet) */
  private readonly sun = { uSunOn: { value: 0 }, uCentre: { value: new THREE.Vector3() }, uSunDir: { value: new THREE.Vector3(0, 1, 0) } };
  readonly lampUniforms = { uTime: { value: 0 }, uNight: { value: 0 }, uFocal: { value: 800 }, uMinPx: { value: FAR_LOD.lampMinPx }, uMaxPx: { value: FAR_LOD.lampMaxPx }, ...this.sun };
  private readonly smokeUniforms = { uTime: { value: 0 }, uRise: { value: FAR_LOD.rise }, uColumn: { value: FAR_LOD.column }, uPuff: { value: FAR_LOD.puff }, uSmoke: { value: new THREE.Color('#a7a199') }, uSmokeNight: { value: new THREE.Color('#2c2a28') }, uFire: { value: new THREE.Color(C.fire) }, uNight: { value: 0 }, uFocal: { value: 800 }, uMinPx: { value: FAR_LOD.puffMinPx }, ...this.sun };
  private dirty = true;
  /** A sheet's look at one tier for every structure (null: by distance). */
  forced: FarTier | null = null;

  constructor(private readonly personWorld: number) { this.group.name = 'farStructures'; }

  /** The structures (built once a set; call again when the set changes). */
  setEntries(entries: readonly FarEntry[]): void {
    this.clear();
    const lampAttrs: number[][] = [[], [], [], [], [], [], []];   // position(3), colour(3), phase, blink, size, shown, day
    const smokeAttrs: number[][] = [[], [], [], [], [], []];       // base(3), up(3), side(3), phase, scale, shown
    const m4 = new THREE.Matrix4(), col = new THREE.Color(), v = new THREE.Vector3();
    for (const entry of entries) {
      const proto = farProto(entry.kind, entry.layout);
      let km = this.kinds.get(entry.kind);
      if (!km) {
        km = { far: new THREE.InstancedMesh(proto.far, this.material, 1), silhouette: new THREE.InstancedMesh(proto.silhouette, this.material, 1), slots: [] };
        for (const im of [km.far, km.silhouette]) { im.frustumCulled = false; im.count = 0; im.name = `far:${entry.kind}`; this.group.add(im); }
        this.kinds.set(entry.kind, km);
      }
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(entry.quaternion);
      m4.compose(entry.position, entry.quaternion, v.setScalar(this.personWorld));
      const slot: Slot = { entry, proto, tier: 'none', hidden: false, lamp0: lampAttrs[0].length / 3, lamps: 0, puff0: smokeAttrs[0].length / 3, puffs: 0,
        centre: entry.position.clone().addScaledVector(up, proto.radius * 0.4 * this.personWorld), up };
      // its lamps (a wreck's are out; the web's light by `lit`)
      if (entry.form !== 'destroyed') for (const l of proto.lamps) {
        if (l.team && entry.lit === false) continue;
        v.set(l.x, l.y, l.z).applyMatrix4(m4);
        col.set(l.team && entry.team ? entry.team : l.color);
        if (l.team && entry.team) col.lerp(new THREE.Color('#ffffff'), 0.35);
        lampAttrs[0].push(v.x, v.y, v.z); lampAttrs[1].push(col.r, col.g, col.b);
        lampAttrs[2].push(((Math.imul(this.slots.length + 1, 2246822519) >>> 0) % 1000) / 1000);   // (a structure's lamps blink together)
        lampAttrs[3].push(l.blink); lampAttrs[4].push(l.size * this.personWorld); lampAttrs[5].push(0); lampAttrs[6].push(l.day);
        slot.lamps++;
      }
      // its smoke: struck a thin column, wrecked a thick one
      if (entry.form === 'damaged' || entry.form === 'destroyed') {
        const n = entry.form === 'destroyed' ? FAR_LOD.puffs : Math.round(FAR_LOD.puffs / 2), scale = (entry.form === 'destroyed' ? 1 : 0.6) * Math.max(0.4, Math.min(1.2, proto.radius / 4)) * this.personWorld;
        v.set(proto.smoke[0], proto.smoke[1] * 0.4, proto.smoke[2]).applyMatrix4(m4);
        // (its fire: a lamp that flickers, seen at night across the planet)
        lampAttrs[0].push(v.x, v.y, v.z); col.set(C.fire); lampAttrs[1].push(col.r, col.g, col.b);
        lampAttrs[2].push(((Math.imul(this.slots.length + 7, 2246822519) >>> 0) % 1000) / 1000); lampAttrs[3].push(3.1);
        lampAttrs[4].push((entry.form === 'destroyed' ? 1.4 : 0.9) * this.personWorld); lampAttrs[5].push(0); lampAttrs[6].push(0.2);
        slot.lamps++;
        v.set(proto.smoke[0], proto.smoke[1], proto.smoke[2]).applyMatrix4(m4);
        const side = new THREE.Vector3(1, 0, 0.3).applyQuaternion(entry.quaternion).normalize();
        for (let k = 0; k < n; k++) {
          smokeAttrs[0].push(v.x, v.y, v.z); smokeAttrs[1].push(up.x, up.y, up.z); smokeAttrs[2].push(side.x, side.y, side.z);
          smokeAttrs[3].push(k / n); smokeAttrs[4].push(scale); smokeAttrs[5].push(0);
          slot.puffs++;
        }
      }
      km.slots.push(slot); this.slots.push(slot); this.byId.set(entry.id, slot);
    }
    for (const km of this.kinds.values()) for (const im of [km.far, km.silhouette]) {
      const n = Math.max(1, km.slots.length);
      // (sized for every slot of the kind: a tier's instances are the first `count`)
      im.instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(n * 16), 16);
      im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    }
    if (lampAttrs[0].length) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(lampAttrs[0], 3));
      ['aColor', 'aPhase', 'aBlink', 'aSize', 'aShown', 'aDay'].forEach((name, i) => g.setAttribute(name, new THREE.Float32BufferAttribute(lampAttrs[i + 1], name === 'aColor' ? 3 : 1)));
      this.lamps = new THREE.Points(g, this.lampMaterial()); this.lamps.frustumCulled = false; this.lamps.name = 'far:lamps'; this.lamps.renderOrder = 2;
      this.group.add(this.lamps);
    }
    if (smokeAttrs[0].length) {
      const g = new THREE.InstancedBufferGeometry(), quad = new THREE.PlaneGeometry(1, 1);
      g.index = quad.index; g.setAttribute('position', quad.getAttribute('position'));
      ['aBase', 'aUp', 'aSide', 'aPhase', 'aScale', 'aShown'].forEach((name, i) => g.setAttribute(name, new THREE.InstancedBufferAttribute(new Float32Array(smokeAttrs[i]), i < 3 ? 3 : 1)));
      g.instanceCount = smokeAttrs[3].length;
      this.smoke = new THREE.Mesh(g, this.smokeMaterial()); this.smoke.frustumCulled = false; this.smoke.name = 'far:smoke'; this.smoke.renderOrder = 1;
      this.group.add(this.smoke);
    }
    this.dirty = true;
  }

  /**
   * Each frame: the camera, the drawing buffer's height (device pixels), how far into night (0 day … 1 night), the time (s);
   * `sun` (play): the planet's centre and the direction to the sun — night is then read where each lamp and plume stands.
   */
  update(camera: THREE.PerspectiveCamera, bufferHeight: number, night: number, t: number, sun?: { centre: THREE.Vector3; dir: THREE.Vector3 }): void {
    this.sun.uSunOn.value = sun ? 1 : 0;
    if (sun) { this.sun.uCentre.value.copy(sun.centre); this.sun.uSunDir.value.copy(sun.dir).normalize(); }
    const focal = (bufferHeight / 2 / Math.tan((camera.fov * Math.PI) / 360)) * (camera.zoom || 1);
    this.lampUniforms.uFocal.value = focal; this.lampUniforms.uNight.value = night; this.lampUniforms.uTime.value = t;
    this.smokeUniforms.uTime.value = t; this.smokeUniforms.uNight.value = night; this.smokeUniforms.uFocal.value = focal;
    const cam = camera.getWorldPosition(_v), h = FAR_LOD.hysteresis;
    for (const s of this.slots) {
      const px = (2 * s.proto.radius * this.personWorld * focal) / Math.max(1e-6, cam.distanceTo(s.centre));
      // (an edge between a tier and the next further: left outward under it × (1 − h), come back in over it × (1 + h))
      const edge = (e: number, near: FarTier) => (rank(s.tier) <= rank(near) ? e * (1 - h) : e * (1 + h));
      const tier: FarTier = this.forced ?? (px >= edge(FAR_LOD.fullPx, 'full') ? 'full' : px >= edge(FAR_LOD.silhouettePx, 'far') ? 'far' : px >= edge(FAR_LOD.dotPx, 'silhouette') ? 'silhouette' : 'none');
      if (tier !== s.tier) { s.tier = tier; this.dirty = true; }
    }
    if (this.dirty) this.restack();
  }

  /** Hide structure `id` from every tier, its lamps and smoke too (the host's fog: an enemy's not revealed). */
  setHidden(id: number | string, hidden: boolean): void {
    const s = this.byId.get(id);
    if (s && s.hidden !== hidden) { s.hidden = hidden; this.dirty = true; }
  }

  /** Whether structure `id` draws its full model now (the host shows its own model and lamps then). */
  isFull(id: number | string): boolean { return this.byId.get(id)?.tier === 'full'; }
  tierOf(id: number | string): FarTier { return this.byId.get(id)?.tier ?? 'none'; }

  /** The instances per tier, their triangles and the draws (the cost table). */
  stats(): { full: number; far: number; silhouette: number; none: number; hidden: number; triangles: number; draws: number; lamps: number; puffs: number } {
    const n = { full: 0, far: 0, silhouette: 0, none: 0, hidden: 0 };
    for (const s of this.slots) if (s.hidden) n.hidden++; else n[s.tier]++;
    let triangles = 0, draws = 0;
    for (const km of this.kinds.values()) for (const im of [km.far, km.silhouette]) if (im.count) { draws++; triangles += im.count * im.geometry.getAttribute('position').count / 3; }
    const lamps = this.lamps ? (this.lamps.geometry.getAttribute('aShown').array as Float32Array).reduce((a, x) => a + x, 0) : 0;
    const puffs = this.smoke ? (((this.smoke.geometry as THREE.InstancedBufferGeometry).getAttribute('aShown').array as Float32Array).reduce((a, x) => a + x, 0)) : 0;
    if (lamps) draws++; if (puffs) draws++;
    return { ...n, triangles, draws, lamps, puffs };
  }

  /** Rewrite the instances: each kind's far and silhouette lists, the lamps and puffs of those not drawn full. */
  private restack(): void {
    this.dirty = false;
    const m4 = new THREE.Matrix4(), sc = new THREE.Vector3(), grey = new THREE.Color('#9a978d'), scorched = new THREE.Color('#3b3632');
    for (const km of this.kinds.values()) {
      let nf = 0, ns = 0;
      for (const s of km.slots) {
        if ((s.tier !== 'far' && s.tier !== 'silhouette') || s.hidden) continue;
        const im = s.tier === 'far' ? km.far : km.silhouette, k = s.tier === 'far' ? nf++ : ns++;
        // (a wreck: its masses slumped to a third of their height, scorched — rubble from afar)
        const wreck = s.entry.form === 'destroyed';
        sc.set(this.personWorld, this.personWorld * (wreck ? 0.33 : 1), this.personWorld);
        im.setMatrixAt(k, m4.compose(s.entry.position, s.entry.quaternion, sc));
        im.setColorAt(k, wreck ? scorched : s.entry.team ?? grey);
      }
      km.far.count = nf; km.silhouette.count = ns;
      // (an instanced mesh of no instances still costs a draw: hidden while empty)
      for (const im of [km.far, km.silhouette]) { im.visible = im.count > 0; im.instanceMatrix.needsUpdate = true; if (im.instanceColor) im.instanceColor.needsUpdate = true; }
    }
    if (this.lamps) {
      const a = this.lamps.geometry.getAttribute('aShown') as THREE.BufferAttribute, arr = a.array as Float32Array;
      for (const s of this.slots) arr.fill(s.tier === 'full' || s.hidden ? 0 : 1, s.lamp0, s.lamp0 + s.lamps);
      a.needsUpdate = true;
      this.lamps.visible = arr.includes(1);
    }
    if (this.smoke) {
      const a = (this.smoke.geometry as THREE.InstancedBufferGeometry).getAttribute('aShown') as THREE.InstancedBufferAttribute, arr = a.array as Float32Array;
      for (const s of this.slots) arr.fill(s.tier === 'full' || s.hidden ? 0 : 1, s.puff0, s.puff0 + s.puffs);
      a.needsUpdate = true;
      this.smoke.visible = arr.includes(1);
    }
  }

  private clear(): void {
    for (const km of this.kinds.values()) for (const im of [km.far, km.silhouette]) { im.removeFromParent(); im.dispose(); }
    this.kinds.clear(); this.slots = []; this.byId.clear();
    for (const o of [this.lamps, this.smoke]) if (o) { o.removeFromParent(); o.geometry.dispose(); }   // (69.98: the materials kept)
    this.lamps = null; this.smoke = null;
  }

  private lampMaterial(): THREE.ShaderMaterial {
    return this.lampMat ??= new THREE.ShaderMaterial({ uniforms: this.lampUniforms, vertexShader: LAMP_VERT, fragmentShader: LAMP_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  }

  private smokeMaterial(): THREE.ShaderMaterial {
    if (!this.smokeMat) {
      this.smokeMat = new THREE.ShaderMaterial({ uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, {}]), vertexShader: SMOKE_VERT, fragmentShader: SMOKE_FRAG, transparent: true, depthWrite: false, fog: true });
      Object.assign(this.smokeMat.uniforms, this.smokeUniforms);
    }
    return this.smokeMat;
  }

  /** 384: the lamps and the plumes on their own materials (made with the first lamp and the first struck structure) — for the shader warm-up (rendering/programWarm.ts). */
  warmParts(): THREE.Object3D[] { return [new THREE.Points(new THREE.BufferGeometry(), this.lampMaterial()), new THREE.Mesh(new THREE.BufferGeometry(), this.smokeMaterial())]; }

  dispose(): void { this.clear(); this.material.dispose(); this.lampMat?.dispose(); this.smokeMat?.dispose(); this.lampMat = this.smokeMat = null; }
}

const _v = new THREE.Vector3();
const rank = (t: FarTier): number => (t === 'full' ? 0 : t === 'far' ? 1 : t === 'silhouette' ? 2 : 3);
